<template>
  <div 
    ref="chartContainer" 
    :style="{ height: height ? `${height}px` : '300px', width: '100%' }"
  ></div>
</template>

<script>
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { 
  TitleComponent, 
  TooltipComponent, 
  GridComponent, 
  LegendComponent ,
  DataZoomComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// Register the required components
echarts.use([
  BarChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  CanvasRenderer,
  DataZoomComponent
]);

export default {
  props: {
    chartData: {
      type: Object,
      default: () => ({ labels: [], datasets: [] })
    },
    height: {
      type: Number,
      default: 400
    },
    options: {
      type: Object,
      default: () => ({})
    },
    graphId: {
      type: Number,
      required: false
    }
  },
  data() {
    return {
      chart: null
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.updateChart();
      }
    }
  },
  mounted() {
    this.initChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    initChart() {
      // Destroy existing chart if it exists
      if (this.chart) {
        this.chart.dispose();
      }

      // Create the chart
      this.chart = echarts.init(this.$refs.chartContainer);

      const dateTickLabels = [];
      this.chartData.labels.forEach(label => {
        let dateLabel = label.split('-').slice(1, 3).reverse().join('/'); // DD/MM/YYYY
        dateTickLabels.push(dateLabel); // Assign the same date label to all sets under the same date
      })
      
      // Prepare the chart options
      const defaultOptions = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          show: true
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dateTickLabels,
          axisLabel: {
            color: '#000000',
            rotate: 45, // Rotate labels for better readability
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#343a40'
          },
        },
        dataZoom: [
          {
            type: 'inside', // Scrollwheel zoom for smooth experience
            start: 100,
            end: 25,
          }
        ],
        series: this.chartData.datasets.map(dataset => ({
          name: dataset.label,
          type: 'bar',
          data: dataset.data,
          barWidth: '60%',
          itemStyle: {
            color: '#73bc44' // Green color
          },
          lineStyle: {
            color: '#73bc44' // Green color for line
          }
        }))
      };

      // Merge default options with provided options
      const mergedOptions = { ...defaultOptions, ...this.options };

      // Render the chart
      this.chart.setOption(mergedOptions);

      // Add click event
      this.chart.on('click', this.onClick);
    },
    updateChart() {
      if (this.chart) {
        this.initChart();
      }
    },
    onClick(params) {
      // Emit event with graph ID and clicked x-axis label
      this.$emit('bar-clicked', { 
        graphId: this.graphId, 
        xAxisLabel: params.name 
      });
    }
  }
}
</script>